import { Container, Graphics } from 'pixi.js';
import { Easings, TweenGroup } from '../../core/Tween';
import type { LineSettlement } from './settleSpin';
import type { WinLine } from './WinLine';

// Phase durations — the line is the *introduction* (tells the eye
// where to look), then dims to a backdrop so the cell celebration
// (SlotSymbol#playWinAnimation, ~1200ms) is the hero. Pre-W6 the
// HOLD phase used a 700ms 0.10↔1.00 sine pulse that bright-flashed
// over the celebration peak frames; we now hold high-alpha briefly
// for the read, snap-dim, and stay dim through the celebration.
//
// Timeline (per line, from spin-settled t=0):
//   0-360ms    DRAW  — sweep on at full alpha, player sees the path
//   360-560ms  HOLD  — full alpha, eye registers the line shape
//   560-760ms  DIM   — drops to backdrop alpha (cells start celebrating)
//   760-1460ms HOLD' — stays dim through cell PEAK (~600-820ms)
//   1460-1600ms FADE — final clear as celebration settles
// Total stays at 1600ms so the take-win pacing is unchanged.
const DRAW_DURATION_MS = 360;
const HOLD_DURATION_MS = 200;
const DIM_DURATION_MS = 200;
const HOLD_DIM_DURATION_MS = 700;
const FADE_DURATION_MS = 140;
// Line weight — was 8px (≈6% of cell width, read as a "spotlight beam"
// that competed with the cell celebration). 4px is half-thickness:
// readable as a polyline shape without dominating the eye, matches the
// "introduce then step aside" intent of the dim-during-celebration
// phase plan.
const STROKE_WIDTH = 4;
/** Alpha the line settles at while the cell win-celebration plays.
 * 0.20 keeps the line shape visible (so the player can still read
 * which cells contributed to the win) without occluding the
 * AnimatedSprite frames underneath. */
const DIMMED_ALPHA = 0.2;

/**
 * Draws glowing polylines through the winning paylines of a settled spin.
 * Mounted as a child of {@link SlotMachine} and rendered above the reels.
 *
 * <p>For each {@link LineSettlement}, the highlighter draws a stroke through
 * the centres of the contributing cells (positions[0..matchCount]) — animated
 * with a draw-on sweep, then a held alpha pulse, then a fade out. Multiple
 * lines render in parallel so a multi-line win reads as celebration rather
 * than a sequence.
 *
 * <p>Lifted-in-spirit from cs-core's win-line reveal pattern. v0 uses simple
 * brand-pink strokes; spritesheet-based win-line frames replace this once the
 * Phase C asset pipeline lands.
 */
export class PaylineHighlighter extends Container {
  private readonly tweens = new TweenGroup();
  private activeGraphics: Graphics[] = [];

  constructor(
    private readonly symbolSize: number,
    private readonly reelSpacing: number,
    /** Theme accent — used for non-wild payline glow. Wild lines stay
     * warm-yellow regardless of theme since the wild is universally a
     * "this is special" cue. */
    private readonly accentColor: number = 0xff6bd6,
  ) {
    super();
  }

  /** Cancel and clear any in-flight highlights. Idempotent. */
  clear(): void {
    this.tweens.cancelAll();
    for (const g of this.activeGraphics) {
      g.destroy();
    }
    this.activeGraphics = [];
  }

  /**
   * Render glowing polylines for every winning payline. Returns a promise that
   * resolves when all line animations have finished — callers use this to
   * pace the result label / take-win sequence.
   */
  async showWinningLines(
    settlements: readonly LineSettlement[],
    winLines: readonly WinLine[],
  ): Promise<void> {
    this.clear();
    if (settlements.length === 0) return;

    const linesById = new Map(winLines.map((line) => [line.id, line]));
    const promises: Array<Promise<void>> = [];
    for (const settlement of settlements) {
      const line = linesById.get(settlement.lineId);
      if (!line) continue;
      promises.push(this.drawSingleLine(line, settlement.matchCount, settlement.hadWild));
    }
    await Promise.all(promises);
  }

  private async drawSingleLine(line: WinLine, matchCount: number, hadWild: boolean): Promise<void> {
    const centres = this.computeCentres(line, matchCount);
    if (centres.length < 2) return;

    const g = new Graphics();
    this.addChild(g);
    this.activeGraphics.push(g);
    const colour = hadWild ? 0xfff59d : this.accentColor;

    // Sweep the stroke on. tweenAsync's await-friendly chain replaces the
    // previous nested onComplete pyramid — the five phases now read
    // top-to-bottom as draw → hold → dim → hold-dim → fade.
    await this.tweens.startAsync({
      durationMs: DRAW_DURATION_MS,
      easing: Easings.easeOutCubic,
      onUpdate: (progress) => this.redrawProgressive(g, centres, progress, colour),
    });
    this.redrawProgressive(g, centres, 1, colour);

    // Hold at full alpha — short window for the eye to register the
    // line shape before the celebration takes over the cells.
    await this.tweens.startAsync({
      durationMs: HOLD_DURATION_MS,
      easing: Easings.linear,
      onUpdate: (_p) => {
        g.alpha = 1;
      },
    });

    // Dim down to backdrop alpha as the cell celebration ramps up. The
    // snap (200ms easeOutCubic) is sharp enough to feel intentional —
    // "stepping aside for the celebration" — without flashing the eye.
    await this.tweens.startAsync({
      durationMs: DIM_DURATION_MS,
      easing: Easings.easeOutCubic,
      onUpdate: (p) => {
        g.alpha = 1 - (1 - DIMMED_ALPHA) * p;
      },
    });

    // Stay dim through the cell celebration peak (cells PEAK around
    // 600-820ms post-spin in the v3 16-frame wakeup-celebration).
    await this.tweens.startAsync({
      durationMs: HOLD_DIM_DURATION_MS,
      easing: Easings.linear,
      onUpdate: (_p) => {
        g.alpha = DIMMED_ALPHA;
      },
    });

    // Final fade as the celebration settles. Short — the line is
    // already at 0.20, so the perceived "clearing out" only needs to
    // cover that remaining alpha.
    await this.tweens.startAsync({
      durationMs: FADE_DURATION_MS,
      easing: Easings.easeOutCubic,
      onUpdate: (p) => {
        g.alpha = DIMMED_ALPHA * (1 - p);
      },
    });
  }

  private computeCentres(line: WinLine, matchCount: number): { x: number; y: number }[] {
    const centres: { x: number; y: number }[] = [];
    for (let i = 0; i < matchCount; i++) {
      const pos = line.positions[i];
      if (!pos) continue;
      centres.push({
        x: pos.col * (this.symbolSize + this.reelSpacing) + this.symbolSize / 2,
        y: pos.row * this.symbolSize + this.symbolSize / 2,
      });
    }
    return centres;
  }

  private redrawProgressive(
    g: Graphics,
    centres: readonly { x: number; y: number }[],
    progress: number,
    colour: number,
  ): void {
    g.clear();
    if (centres.length < 2) return;

    // Walk the polyline in path-length order, drawing as much as `progress`
    // of the total path length. Smooth draw-on rather than per-segment popping.
    const segments: { from: { x: number; y: number }; to: { x: number; y: number }; length: number }[] = [];
    let totalLength = 0;
    for (let i = 0; i < centres.length - 1; i++) {
      const from = centres[i]!;
      const to = centres[i + 1]!;
      const length = Math.hypot(to.x - from.x, to.y - from.y);
      segments.push({ from, to, length });
      totalLength += length;
    }
    const targetLength = totalLength * progress;
    let walked = 0;

    g.moveTo(centres[0]!.x, centres[0]!.y);
    for (const seg of segments) {
      if (walked + seg.length <= targetLength) {
        g.lineTo(seg.to.x, seg.to.y);
        walked += seg.length;
      } else {
        const remaining = targetLength - walked;
        const t = Math.max(0, remaining / seg.length);
        const x = seg.from.x + (seg.to.x - seg.from.x) * t;
        const y = seg.from.y + (seg.to.y - seg.from.y) * t;
        g.lineTo(x, y);
        break;
      }
    }
    g.stroke({ color: colour, width: STROKE_WIDTH, alpha: 0.95, cap: 'round', join: 'round' });
    g.alpha = 1;
  }
}
